/**
 * Owns the AI Chat input surface only: local counter, attachment preview and
 * browser gestures. Uploading and message submission stay in injected controllers.
 */
export function createInputSurfaceController({
  elements,
  attachmentController,
  renderAttachmentPreview,
  onRetryTools,
  onRetrySkills,
  getCurrentRoleCode,
  documentRef = document,
}) {
  const {
    textarea,
    counter,
    sendButton,
    plusButton,
    fileInput,
    attachmentsPreview,
    inputArea,
    dragOverlay,
    toolsButton,
    toolsItems,
    skillsItems,
  } = elements;
  let dragCounter = 0;
  let bound = false;

  function updateCounter() {
    if (!textarea || !counter || !sendButton) return;
    const length = textarea.value.length;
    counter.textContent = `${length}/8000`;
    counter.className = length > 7200 ? 'aichat-input-counter aichat-counter-warn' : 'aichat-input-counter';
    sendButton.disabled = (length === 0 && attachmentController.getPendingAttachments().length === 0) || length > 8000;
  }

  function renderPreview() {
    if (!attachmentsPreview) return;
    const visible = renderAttachmentPreview(attachmentsPreview, attachmentController.getState());
    attachmentsPreview.hidden = !visible;
  }

  function sync() {
    renderPreview();
    updateCounter();
  }

  function renderToolsAlwaysOn() {
    if (!toolsButton) return;
    toolsButton.classList.add('active');
    toolsButton.setAttribute('aria-pressed', 'true');
    toolsButton.title = '联网搜索已开启';
  }

  function handleFileChange() {
    const files = Array.from(fileInput?.files || []);
    if (fileInput) fileInput.value = '';
    return attachmentController.processFiles(files);
  }

  function handleAttachmentPreviewClick(event) {
    const button = event.target.closest('.aichat-attachment-remove');
    if (!button) return;
    attachmentController.removePendingAttachment(Number(button.dataset.index));
  }

  function showDragOverlay(event) {
    event.preventDefault();
    dragCounter += 1;
    dragOverlay?.classList.add('aichat-show');
  }

  function hideDragOverlay(event) {
    event.preventDefault();
    dragCounter -= 1;
    if (dragCounter <= 0) {
      dragOverlay?.classList.remove('aichat-show');
      dragCounter = 0;
    }
  }

  function allowDrag(event) {
    event.preventDefault();
  }

  function handleDrop(event) {
    event.preventDefault();
    dragOverlay?.classList.remove('aichat-show');
    dragCounter = 0;
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length) void attachmentController.processFiles(files);
  }

  function handlePaste(event) {
    if (!inputArea || inputArea.hidden) return;
    const imageFiles = [];
    for (const item of event.clipboardData?.items || []) {
      if (item.kind !== 'file' || !item.type.startsWith('image/')) continue;
      const file = item.getAsFile();
      if (file) imageFiles.push(file);
    }
    if (!imageFiles.length) return;
    event.preventDefault();
    void attachmentController.processFiles(imageFiles);
  }

  function resizeTextarea() {
    if (!textarea) return;
    // field-sizing covers wrapped content in modern browsers; rows keeps the
    // same multiline affordance for browsers that do not support it yet.
    textarea.rows = Math.min(6, Math.max(1, textarea.value.split('\n').length));
  }

  function handleToolsRetry(event) {
    if (event.target.closest("[data-action='retry-load-tools']")) onRetryTools?.();
  }

  function handleSkillsRetry(event) {
    if (event.target.closest("[data-action='retry-load-skills']")) onRetrySkills?.(getCurrentRoleCode?.());
  }

  function bind() {
    if (bound) return;
    plusButton?.addEventListener('click', () => fileInput?.click());
    fileInput?.addEventListener('change', handleFileChange);
    attachmentsPreview?.addEventListener('click', handleAttachmentPreviewClick);
    inputArea?.addEventListener('dragenter', showDragOverlay);
    inputArea?.addEventListener('dragleave', hideDragOverlay);
    inputArea?.addEventListener('dragover', allowDrag);
    inputArea?.addEventListener('drop', handleDrop);
    documentRef.addEventListener('paste', handlePaste);
    textarea?.addEventListener('input', resizeTextarea);
    toolsItems?.addEventListener('click', handleToolsRetry);
    skillsItems?.addEventListener('click', handleSkillsRetry);
    bound = true;
  }

  return {
    bind,
    sync,
    updateCounter,
    renderToolsAlwaysOn,
  };
}
